<template>
  <div class="md-page">

    <div class="md-info-banner">
        <i class="fab fa-instagram"></i>
        <span>My Data is only available for Instagram channels</span>
    </div>

    <div class="md-header">
      <div>
        <h1 class="md-title">My Data</h1>
        <p class="md-sub">Your Instagram audience &amp; engagement statistics</p>
      </div>
      <div v-if="instagramChannels.length > 1" class="md-switcher">
        <button
          v-for="ch in instagramChannels"
          :key="ch.uuid"
          class="md-switch-btn"
          :class="{ 'md-switch-btn--active': selectedUuid === ch.uuid }"
          @click="selectChannel(ch)"
        >
          @{{ ch.username }}
        </button>
      </div>
    </div>

    <div v-if="channel && stats" class="md-date-range">
      <i class="fal fa-calendar-alt"></i>
          Your data is from the last 14 days -
      <span class="md-date-range-dates">{{ dateRange }}</span>
    </div>

    <template v-if="pageLoading">
      <div class="skel-row" style="height:120px;border-radius:var(--r-lg);margin-bottom:16px;"></div>
      <div class="md-stat-grid">
        <div v-for="n in 6" :key="n" class="skel-row" style="height:80px;border-radius:var(--r-lg);"></div>
      </div>
    </template>

    <div v-else-if="instagramChannels.length === 0" class="md-empty">
      <i class="fab fa-instagram md-empty-icon"></i>
      <div class="md-empty-title">No Instagram connected</div>
      <div class="md-empty-sub">Connect your Instagram account to see audience data.</div>
      <router-link :to="{ name: 'channel.create' }" class="btn btn-primary btn-sm" style="margin-top:14px;">
        Connect channel
      </router-link>
    </div>

    <template v-else-if="channel">

      <div class="md-profile-card">
        <channel-profile-avatar :user="user" :channel="channel" class="md-avatar-wrap"></channel-profile-avatar>
        <div class="md-profile-body">
          <div class="md-profile-name">{{ user.first_name }} {{ user.last_name }}</div>
          <div class="md-profile-handle">
            <i class="fab fa-instagram" style="font-size:13px;margin-right:5px;"></i>{{ channel.username }}
          </div>
          <div v-if="channel.categories && channel.categories.length" class="md-cats">
            <span v-for="cat in channel.categories" :key="cat.id" class="md-cat-chip">
              {{ cat.display_name }}
            </span>
          </div>
        </div>
        <div class="md-profile-meta">
          <div v-if="user.influencer && user.influencer.birthday" class="md-meta-row">
            <span class="md-meta-label">Age</span>
            <span class="md-meta-val">{{ user.influencer.birthday | date_to_age }}</span>
          </div>
          <div v-if="user.influencer && user.influencer.gender" class="md-meta-row">
            <span class="md-meta-label">Gender</span>
            <span class="md-meta-val">{{ user.influencer.gender === 'MALE' ? 'Male' : 'Female' }}</span>
          </div>
        </div>
      </div>

      <div v-if="stats" class="md-stat-grid">
        <div class="md-stat-card">
          <i class="fab fa-instagram md-stat-icon"></i>
          <div class="md-stat-val">{{ channel.followers | local_numbers }}</div>
          <div class="md-stat-label">Followers</div>
        </div>
        <div class="md-stat-card">
          <i class="fal fa-images md-stat-icon"></i>
          <div class="md-stat-val">{{ channel.media_count | local_numbers }}</div>
          <div class="md-stat-label">Feed posts</div>
        </div>
        <div class="md-stat-card">
          <i class="fal fa-bullseye md-stat-icon"></i>
          <div class="md-stat-val">{{ reach | friendly_numbers }}</div>
          <div class="md-stat-label">Reach</div>
        </div>
        <div class="md-stat-card">
          <i class="fal fa-eye md-stat-icon"></i>
          <div class="md-stat-val">{{ views | friendly_numbers }}</div>
          <div class="md-stat-label">Views</div>
        </div>
        <div class="md-stat-card">
          <i class="fal fa-circle-notch md-stat-icon"></i>
          <div class="md-stat-val">{{ storyReach | friendly_numbers }}</div>
          <div class="md-stat-label">Story reach</div>
        </div>
        <div class="md-stat-card">
          <i class="fal fa-eye md-stat-icon"></i>
          <div class="md-stat-val">{{ storyImpressions | friendly_numbers }}</div>
          <div class="md-stat-label">Story impressions</div>
        </div>
      </div>
      <div v-else class="md-stat-grid">
        <div v-for="n in 6" :key="n" class="skel-row" style="height:80px;border-radius:var(--r-lg);"></div>
      </div>

      <template v-if="stats">
        <div class="md-chart-row">
          <div class="tbl-card">
            <div class="tbl-card-header">
              <div>
                <div class="tbl-card-title">Follower countries</div>
                <div class="tbl-card-sub">Top 5 follower countries</div>
              </div>
            </div>
            <influencer-channel-follower-country-chart :value="stats.countries.slice(0, 5)"></influencer-channel-follower-country-chart>
          </div>
          <div class="tbl-card">
            <div class="tbl-card-header">
              <div>
                <div class="tbl-card-title">Follower cities</div>
                <div class="tbl-card-sub">Top 5 follower cities</div>
              </div>
            </div>
            <influencer-channel-follower-city-chart :value="stats.cities.slice(0, 5)"></influencer-channel-follower-city-chart>
          </div>
        </div>

        <div class="tbl-card" style="margin-top:16px;">
          <div class="tbl-card-header">
            <div>
              <div class="tbl-card-title">Daily follower growth</div>
              <div class="tbl-card-sub">Last 14 days</div>
            </div>
          </div>
          <div style="padding:0 16px 16px;">
            <follower-growth-chart min-height="220px" :data="followerGrowthData"></follower-growth-chart>
          </div>
        </div>

        <div class="md-chart-row" style="margin-top:16px;">
          <div class="tbl-card">
            <div class="tbl-card-header">
              <div class="tbl-card-title">Gender distribution</div>
            </div>
            <influencer-channel-follower-gender-chart :value="stats.gender_age"></influencer-channel-follower-gender-chart>
          </div>
          <div class="tbl-card">
            <div class="tbl-card-header">
              <div class="tbl-card-title">Age &amp; gender</div>
            </div>
            <influencer-channel-follower-gender-age-chart :value="stats.gender_age"></influencer-channel-follower-gender-age-chart>
          </div>
        </div>
      </template>

    </template>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import FollowerGrowthChart from "../../charts/FollowerGrowthChart";
import InfluencerChannelFollowerCountryChart from "../../charts/InfluencerChannelFollowerCountryChart";
import InfluencerChannelFollowerCityChart from "../../charts/InfluencerChannelFollowerCityChart";
import InfluencerChannelFollowerGenderChart from "../../charts/InfluencerChannelFollowerGenderChart";
import InfluencerChannelFollowerGenderAgeChart from "../../charts/InfluencerChannelFollowerGenderAgeChart";
import ChannelProfileAvatar from "../../avatars/InfluencerChannelAvatar";
import date_to_age from "@/helpers/filters/date_to_age";
import friendly_numbers from "@/helpers/filters/friendly_numbers";
import local_numbers from "@/helpers/filters/local_numbers";

export default {
  components: {
    ChannelProfileAvatar,
    FollowerGrowthChart,
    InfluencerChannelFollowerCountryChart,
    InfluencerChannelFollowerCityChart,
    InfluencerChannelFollowerGenderChart,
    InfluencerChannelFollowerGenderAgeChart,
  },
  filters: { date_to_age, friendly_numbers, local_numbers },
  data: () => ({
    allChannels: [],
    selectedUuid: null,
    channel: null,
    stats: null,
    pageLoading: true,
  }),
  computed: {
    ...mapState("core/auth", ["user"]),
    instagramChannels() {
      return this.allChannels.filter(
        c => c.active && c.channel && c.channel.name === "instagram"
      );
    },
    reach() {
      if (!this.stats) return 0;
      return this.stats.daily.reduce((s, e) => s + (e.unique_profile_views || 0), 0);
    },
    views() {
      if (!this.stats) return 0;
      return this.stats.daily.reduce((s, e) => s + (e.views || 0), 0);
    },
    storyReach() {
      if (!this.stats) return 0;
      return this.stats.daily.reduce((s, e) => s + (e.story_reach || 0), 0);
    },
    storyImpressions() {
      if (!this.stats) return 0;
      return this.stats.daily.reduce((s, e) => s + (e.story_impressions || 0), 0);
    },
    followerGrowthData() {
      if (!this.stats) return { dates: [], values: [] };
      const daily = this.stats.daily.slice(0, 14);
      const data = { dates: [], values: [] };
      daily.forEach(v => { data.dates.push(v.date); data.values.push(v.followers); });
      data.dates.reverse();
      data.values.reverse();
      return data;
    },
    dateRange() {
      if (!this.stats || !this.stats.daily || !this.stats.daily.length) return '';
      const daily = this.stats.daily;
      // daily is newest-first (see followerGrowthData which reverses it)
      const newest = daily[0].date;
      const oldest = daily[Math.min(13, daily.length - 1)].date;
      const fmtShort = d => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      const fmtFull  = d => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      return `${fmtShort(oldest)} - ${fmtFull(newest)}`;
    },
  },
  methods: {
    ...mapActions("influencer", ["getInfluencerChannels", "getSingleChannel"]),
    ...mapActions("core", ["getInfluencerStats"]),
    ...mapMutations("influencer", { setChannels: "SET_CHANNELS" }),
    async selectChannel(ch) {
      this.selectedUuid = ch.uuid;
      this.channel = ch;
      this.stats = null;
      const statsResult = await this.getInfluencerStats({ influencer_channel_uuid: ch.uuid });
      this.stats = statsResult;
    },
  },
  async created() {
    this.pageLoading = true;
    const data = await this.getInfluencerChannels({ influencer_uuid: this.user.influencer.uuid });
    this.allChannels = data;
    this.setChannels(data);
    const ig = data.filter(c => c.active && c.channel && c.channel.name === "instagram");
    if (ig.length > 0) {
      await this.selectChannel(ig[0]);
    }
    this.pageLoading = false;
  },
};
</script>

<style scoped>
.md-page {
  max-width: var(--maxw);
  margin: 0 auto;
  padding: 24px 20px 48px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.md-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 4px;
}
.md-title { font-size: 22px; font-weight: 650; color: var(--ink-1); margin: 0 0 2px; letter-spacing: -.02em; }
.md-sub { font-size: 13.5px; color: var(--ink-3); margin: 0; }

.md-info-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #f0f4ff;
  border: 1px solid #d0d9f5;
  border-radius: var(--r-sm);
  font-size: 13px;
  color: #3d5a99;
}

.md-switcher { display: flex; gap: 8px; flex-wrap: wrap; }
.md-switch-btn {
  padding: 6px 14px;
  border-radius: 99px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink-2);
  font-size: 13px;
  cursor: pointer;
  transition: background .15s, color .15s, border-color .15s;
}
.md-switch-btn:hover { border-color: var(--accent); color: var(--accent); }
.md-switch-btn--active { background: var(--accent); color: #fff; border-color: var(--accent); }

.md-profile-card {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  padding: 20px 24px;
  flex-wrap: wrap;
}
.md-avatar-wrap { flex-shrink: 0; }
.md-profile-body { flex: 1; min-width: 160px; }
.md-profile-name { font-size: 16px; font-weight: 650; color: var(--ink-1); margin-bottom: 2px; }
.md-profile-handle { font-size: 13px; color: var(--ink-3); margin-bottom: 10px; }
.md-cats { display: flex; flex-wrap: wrap; gap: 6px; }
.md-cat-chip {
  font-size: 11px; font-weight: 500;
  padding: 3px 10px; border-radius: 99px;
  border: 1px solid var(--line);
  color: var(--ink-2);
  background: var(--surface);
}
.md-profile-meta { display: flex; flex-direction: column; gap: 6px; }
.md-meta-row { display: flex; gap: 8px; align-items: center; }
.md-meta-label { font-size: 12px; color: var(--ink-3); min-width: 48px; }
.md-meta-val { font-size: 13px; font-weight: 500; color: var(--ink-1); }

.md-stat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
@media (max-width: 600px) {
  .md-stat-grid { grid-template-columns: repeat(2, 1fr); }
}
.md-stat-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.md-stat-icon { font-size: 18px; color: var(--ink-3); margin-bottom: 6px; }
.md-stat-val { font-size: 22px; font-weight: 700; color: var(--ink-1); letter-spacing: -.03em; }
.md-stat-label { font-size: 12px; color: var(--ink-3); font-weight: 500; }

.md-chart-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
@media (max-width: 700px) {
  .md-chart-row { grid-template-columns: 1fr; }
}

.md-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  color: var(--ink-3);
  text-align: center;
}
.md-empty-icon { font-size: 40px; margin-bottom: 14px; opacity: .5; }
.md-empty-title { font-size: 16px; font-weight: 600; color: var(--ink-2); margin-bottom: 6px; }
.md-empty-sub { font-size: 13px; max-width: 320px; }

.md-skel-profile {
  height: 120px;
  background: var(--line);
  border-radius: var(--r-lg);
  animation: pulse 1.4s ease-in-out infinite;
}
@keyframes pulse { 0%,100%{opacity:.6} 50%{opacity:1} }

.tbl-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  overflow: hidden;
}
.tbl-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid var(--line);
}
.tbl-card-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--ink-1);
  margin-bottom: 2px;
}
.tbl-card-sub {
  font-size: 12px;
  color: var(--ink-3);
}

.md-date-range {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  color: var(--ink-3);
  padding: 9px 14px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-sm);
}
.md-date-range-dates {
  font-weight: 600;
  color: var(--ink-2);
}
</style>